import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import ShareWheelModal from '../components/wheels/ShareWheelModal';
import WheelDisplay from '../components/wheels/WheelDisplay';
import { useAuth } from '../hooks/useAuth';
import { wheelsApi } from '../services/api';
import { getStoredWheelById, incrementSpinCount } from '../services/localStorage';
import { Wheel, WheelEntry } from '../types/wheel';
import { trackWheelSpin } from '../utils/trackingUtils';

const WheelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const [wheel, setWheel] = useState<Wheel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [spinResult, setSpinResult] = useState<WheelEntry | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  // For notification system
  const [notification, setNotification] = useState<{ message: string; visible: boolean }>({
    message: '',
    visible: false,
  });

  useEffect(() => {
    const fetchWheel = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // First try to get from localStorage
        const localWheel = getStoredWheelById(id);

        if (localWheel) {
          setWheel(localWheel);
          setError(null);
          setLoading(false);
          return;
        }

        // If not in localStorage, try the API
        const response = await wheelsApi.getById(id);
        setWheel(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching wheel:', err);
        setError('Failed to load wheel. It may have been deleted or does not exist.');
      } finally {
        setLoading(false);
      }
    };

    fetchWheel();
  }, [id]);

  const handleSpinComplete = (entry: WheelEntry) => {
    setSpinResult(entry);

    // Record the spin
    if (wheel && id) {
      // Track wheel spin event
      trackWheelSpin(id, wheel.name, entry.text);

      // Check if it's a local wheel
      if ('isLocal' in wheel) {
        // Update in localStorage
        const updatedWheel = incrementSpinCount(id);
        if (updatedWheel) {
          setWheel(updatedWheel);
        }
      } else {
        // Record in the backend API
        wheelsApi.spin(id, { entry_id: entry.id }).catch(err => {
          console.error('Error recording spin:', err);
        });
      }
    }
  };

  // Share functionality
  const handleShare = () => {
    if (!wheel) return;
    // Open the share modal
    setShareModalOpen(true);
  };

  // Close share modal
  const handleCloseShareModal = () => {
    setShareModalOpen(false);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Link
          to={isAuthenticated || (wheel && !('isLocal' in wheel)) ? '/my-wheels' : '/wheels'}
          className="inline-flex items-center text-primary hover:text-primary-dark mb-6"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back to Wheels
        </Link>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Wheel details */}
        {!loading && wheel && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <h1 className="text-3xl font-bold text-dark mb-2">{wheel.name}</h1>
              <div className="text-gray-500">
                <span className="mr-4 text-accent-dark font-semibold">Wheel Details</span>
                <span className="mr-4">
                  <span role="img" aria-label="Entries">
                    📝
                  </span>{' '}
                  {wheel.entries.length} entries
                </span>
                <span>
                  <span role="img" aria-label="Spins">
                    🔄
                  </span>{' '}
                  {wheel.spinCount} spins
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Wheel display */}
              <div className="flex justify-center flex-col items-center">
                <WheelDisplay
                  entries={wheel.entries}
                  onSpinComplete={handleSpinComplete}
                  size={400}
                  colorScheme={wheel.theme}
                  spinButtonText="Spin Wheel"
                />

                <div className="flex gap-4 mt-4">
                  {/* Share button - show for authenticated users or cloud-stored wheels */}
                  {(isAuthenticated || !('isLocal' in wheel)) && (
                    <button
                      onClick={handleShare}
                      className="px-8 py-3 text-lg bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors"
                    >
                      Share Wheel
                    </button>
                  )}

                  {/* Edit button */}
                  <Link
                    to={`/edit-wheel/${wheel.id}`}
                    state={{ storageType: 'isLocal' in wheel ? 'local' : 'cloud' }}
                    className="px-8 py-3 text-lg bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors"
                  >
                    Edit Wheel
                  </Link>
                </div>
              </div>

              {/* Wheel information */}
              <div>
                <div className="card p-6 mb-6">
                  <h2 className="text-xl font-semibold mb-4">Options</h2>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {wheel.entries.map(entry => (
                      <div
                        key={entry.id}
                        className="p-3 rounded-lg flex items-center"
                        style={{
                          backgroundColor: entry.color || '#f3f4f6',
                          color: entry.color ? '#ffffff' : '#374151',
                        }}
                      >
                        {entry.emoji && <span className="text-xl mr-2">{entry.emoji}</span>}
                        <span>{entry.text}</span>
                        {entry.weight > 1 && (
                          <span className="ml-auto text-sm opacity-75">
                            Weight: {entry.weight}x
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Spin result */}
                {spinResult && (
                  <div className="card p-6 border-2 border-primary">
                    <h2 className="text-xl font-semibold mb-2">Results</h2>
                    <div className="text-2xl font-bold text-primary">
                      {spinResult.emoji && <span className="mr-2">{spinResult.emoji}</span>}
                      {spinResult.text}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification */}
      {notification.visible && (
        <div className="fixed bottom-4 right-4 bg-dark text-white px-4 py-3 rounded-lg shadow-lg animate-fade-in">
          {notification.message}
        </div>
      )}

      {/* Share Wheel Modal */}
      {wheel && (
        <ShareWheelModal wheel={wheel} isOpen={shareModalOpen} onClose={handleCloseShareModal} />
      )}
    </Layout>
  );
};

export default WheelDetail;
